import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import SEOHead from './components/SEOHead';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import ForWhomSection from './components/ForWhomSection';
import TestimonialsSection from './components/TestimonialsSection';
import BookingSection from './components/BookingSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import MultiStepForm from './components/MultiStepForm';
import CookieBanner from './components/CookieBanner';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TestimonialsPage from './pages/TestimonialsPage';
import { translations, heroImage } from './data/mockData';
import './App.css';

function HomePage({ currentLang, onLanguageToggle, openConsultationForm, t }) {
  // When the page loads with a hash like /#about, scroll to that section.
  // (Used when the user clicks "About" from another page like /temoignages.)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    // Wait one tick so the sections have actually rendered.
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <SEOHead />
      <Header t={t} currentLang={currentLang} onLanguageToggle={onLanguageToggle} />
      <main>
        <HeroSection t={t} heroImage={heroImage} onOpenConsultationForm={openConsultationForm} />
        <AboutSection t={t} />
        <ServicesSection language={currentLang} onOpenConsultationForm={openConsultationForm} />
        <ForWhomSection t={t} />
        <TestimonialsSection language={currentLang} />
        <BookingSection t={t} onOpenConsultationForm={openConsultationForm} />
        <ContactSection t={t} />
      </main>
      <Footer t={t} />
    </>
  );
}

function App() {
  const [currentLang, setCurrentLang] = useState('fr');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [preSelectedService, setPreSelectedService] = useState(null);

  const toggleLanguage = () => {
    setCurrentLang((prev) => (prev === 'fr' ? 'en' : 'fr'));
  };

  const openConsultationForm = (selectedService = null) => {
    setIsFormOpen(true);
    setPreSelectedService(selectedService);
  };

  const closeConsultationForm = () => {
    setIsFormOpen(false);
  };

  const t = translations[currentLang];

  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="App">
          <Toaster position="top-right" richColors />
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  currentLang={currentLang}
                  onLanguageToggle={toggleLanguage}
                  openConsultationForm={openConsultationForm}
                  t={t}
                />
              }
            />
            <Route path="/politique-de-confidentialite" element={
              <>
                <SEOHead 
                  title="Politique de confidentialité — Glow & You"
                  description="Politique de confidentialité et protection des données personnelles de Glow & You"
                />
                <PrivacyPolicy />
              </>
            } />
            <Route path="/temoignages" element={
              <>
                <SEOHead 
                  title="Témoignages — Glow & You"
                  description="Découvrez les transformations réelles de notre clientèle. Des personnes authentiques partagent leur expérience avec Glow & You."
                />
                <Header t={t} currentLang={currentLang} onLanguageToggle={toggleLanguage} />
                <TestimonialsPage language={currentLang} />
                <Footer t={t} />
              </>
            } />
          </Routes>
          <MultiStepForm
            isOpen={isFormOpen}
            onClose={closeConsultationForm}
            language={currentLang}
            preSelectedService={preSelectedService}
          />
          <CookieBanner />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
